import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { TokenService } from 'src/app/core/services/token.service';
import { MaterialModule } from 'src/app/material.module';
import { ApiMessageService } from 'src/app/shared/services/api-message.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { ContactType } from '../../../models/contact/contactType';

@Component({
  selector: 'app-contact-detail',
  standalone: true,
  imports: [
    MaterialModule,
    TablerIconsModule,
    MatNativeDateModule,
    NgScrollbarModule,
    CommonModule,
    RouterModule,
    TranslateModule,
    MatCardModule,
    MatGridListModule,
    MatFormFieldModule,
    ReactiveFormsModule
  ],
  templateUrl: './contact-detail.component.html'
})
export class ContactDetailComponent implements OnChanges {
  @Output() addComplete = new EventEmitter<void>();
  @Input() detailsData: any = {} //this for update data 
  @Input() contactInfoId: number;
  @Input() apiName: string;
  contactDetailForm: FormGroup;
  contactTypeList: ContactType[] = [];
  currentLang: any = 'ar';
  loading: boolean = false;
  userId: number;
  langCode: string;
  showAddInfoButton: Boolean = true;
  showEditInfoButton: Boolean = false;
  showSaveInfoButton: Boolean = false;
  action: any = 'Add'
  backBtnName: string;
  isArabic: boolean;

  constructor(private fb: FormBuilder,
    private router: Router,
    private sharedService: SharedService,
    public translate: TranslateService,
    private tokenService: TokenService,
    public apiMessageService: ApiMessageService
  ) {
    this.contactDetailForm = this.fb.group({
      contactInfoId: [1, Validators.required],
      contactTypeId: ['', Validators.required],
      contactValue: ['', Validators.required],
      orderNo: ['', Validators.required],
      isActive: [true],
    });

    let lang: any = localStorage.getItem('lang');
    let langObj: any;
    if (lang != undefined && lang != null) {

      langObj = JSON.parse(lang);
      this.langCode = langObj.code;
    }
    else {
      this.langCode = 'ar';
    }

    this.translate.use(this.langCode);
    this.userId = this.tokenService.getUserIdFromToken();

    this.isArabic = this.langCode === 'ar';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['detailsData'] && this.detailsData) {
      this.action = this.detailsData.action
      this.contactDetailForm.patchValue(this.detailsData);
    }
  }

  ngOnInit(): void {
    this.sharedService.getDropDownList("contact/type", true).subscribe((response: any) => {
      this.contactTypeList = response.data;
    });

    this.setBackButtonName(this.apiName);
  }

  AddContactDetail() {
    this.loading = true;
    if (this.contactDetailForm.valid) {
      const details = this.contactDetailForm.value;
      let contactDetailData = {
        contactInfoId: this.contactInfoId,
        contactTypeId: details.contactTypeId,
        contactValue: details.contactValue,
        orderNo: details.orderNo,
        isActive: details.isActive,
        creationDate: new Date(),
        creationUserId: this.userId,
      }

      this.sharedService.create("contact/detail", contactDetailData).subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.apiMessageService.getMessage(response.message);
          } else if (response.isFailure) {
            this.apiMessageService.getMessages(response.errors);
          }
          this.addComplete.emit();
        },
        error: (err) => {
          this.apiMessageService.handleError(err);
          this.loading = false
        },
        complete: () => {
          this.loading = false; // Stop loading
          this.cancelAddOrUpdate();
        }
      });
    }
  }

  UpdateDetailsRowData(): void {
    this.loading = true;
    if (this.contactDetailForm.valid) {
      const details = this.contactDetailForm.value;
      let contactDetailData = {
        id: this.detailsData.id,
        contactInfoId: this.contactInfoId,
        contactTypeId: details.contactTypeId,
        contactValue: details.contactValue,
        orderNo: details.orderNo,
        isActive: details.isActive,
        creationDate: new Date(),
        creationUserId: this.userId,
        lastUpdateUserId: this.detailsData.isActive ? this.userId : this.detailsData.lastUpdateUserId,
        lastUpdateDate: this.detailsData.lastUpdateDate,
        deactivateUserId: !this.detailsData.isActive ? this.userId : this.detailsData.deactivateUserId,
        deactivateDate: this.detailsData.deactivateDate,
      }

      this.sharedService.update("contact/detail", contactDetailData).subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.apiMessageService.getMessage(response.message);
          } else if (response.isFailure) {
            this.apiMessageService.getMessages(response.errors);
          }
          this.addComplete.emit();
        },
        error: (err) => {
          this.apiMessageService.handleError(err);
          this.loading = false
        },
        complete: () => {
          this.loading = false; // Stop loading
          this.cancelAddOrUpdate()
        }
      });
    }
  }

  cancelAddOrUpdate() {
    this.contactDetailForm.reset({
      contactInfoId: this.contactInfoId,
      isActive: true
    });

    Object.keys(this.contactDetailForm.controls).forEach(key => {
      const control = this.contactDetailForm.get(key);
      control?.setErrors(null);
      control?.markAsPristine();
      control?.markAsUntouched();
    });

    this.detailsData = {};
    this.loading = false;

    this.action = 'Add';
  }

  onSubmit() {
    if (this.action === 'Update') {
      this.UpdateDetailsRowData();
    } else {
      this.AddContactDetail();
    }
  }

  onToggleChange(event: any, element: any) {
    const value = element.get('isActive')?.value;
    // if (event.checked) {
    //   this.UpdateStatusRowData(element);
    // }
  }

  setBackButtonName(apiName: string) {
    if (apiName == "Store") {
      this.backBtnName = "Lookups.Stores.BackToStorePage";
    }
    else if (apiName == "Event") {
      this.backBtnName = "BackToEventPage";
    }
    else if (apiName == "Community") {
      this.backBtnName = "Community.BackToCommunityPage";
    }
  }

  navigateToAmenityPage() {
    if (this.apiName == "Store") {
      this.router.navigate(['swf/store']);
    }
    else if (this.apiName == "Event") {
      this.router.navigate(['swf/event']);
    }
    else if (this.apiName == "Community") {
      this.router.navigate(['swf/community']);
    }
  }
}
